import { memo, useEffect, useMemo, useRef, useState } from "react";

import maplibregl from "maplibre-gl";
import type { FeatureCollection } from "geojson";
import "maplibre-gl/dist/maplibre-gl.css";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import CapabilityGate from "@/components/CapabilityGate";
import LockedFeatureCard from "@/components/pricing/LockedFeatureCard";
import { useCapabilities } from "@/hooks/useCapabilities";
import { compactCityLabel, formatCityHeader } from "@/lib/geography";
import type { OsrmRouteSummary, RoutePlan } from "@/types/routing";

const LARGE_MARKET_COUNT = 3;
const LARGE_MARKET_SCORE = 0.7;

function isLargeMarket(city: RoutePlan["cities"][number]) {
  const venueCount = city.venue_ids?.length ?? city.venue_names?.length ?? 0;
  if (venueCount >= LARGE_MARKET_COUNT) {
    return true;
  }
  if (typeof city.market_score === "number") {
    return city.market_score >= LARGE_MARKET_SCORE;
  }
  return false;
}

type RoutingMapProps = {
  plan: RoutePlan | null;
  useRealRouting: boolean;
  onToggleRealRouting: (value: boolean) => void;
  routeSummary: OsrmRouteSummary | null;
  routeError: string | null;
  maxDriveHours?: number | null;
  needsRefresh?: boolean;
};

export const RoutingMap = memo(function RoutingMap({ plan, useRealRouting, onToggleRealRouting, routeSummary, routeError, maxDriveHours, needsRefresh }: RoutingMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const { capabilities, openUpgradeModal } = useCapabilities();
  const canUseOsrm = capabilities.canUseRoutingOsrm;
  const canViewWarnings = capabilities.canViewRoutingWarnings;
  const [viewMode, setViewMode] = useState<"clustered" | "scattered">("clustered");

  const mappedCities = useMemo(() => {
    if (!plan?.cities?.length) return [];
    return plan.cities.map((city) => ({
      ...city,
      header: formatCityHeader(city.city, city.state),
      venueCount: city.venue_ids?.length ?? city.venue_names?.length ?? 0,
    }));
  }, [plan]);

  const clusterCollection = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: mappedCities
        .filter(isLargeMarket)
        .map((city) => ({
          type: "Feature" as const,
          geometry: {
            type: "Point" as const,
            coordinates: [city.longitude, city.latitude] as [number, number],
          },
          properties: {
            name: city.header,
            count: Math.max(city.venueCount, 1),
          },
        })),
    }),
    [mappedCities],
  );

  const outlierCollection = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: mappedCities
        .filter((city) => !isLargeMarket(city))
        .map((city) => ({
          type: "Feature" as const,
          geometry: {
            type: "Point" as const,
            coordinates: [city.longitude, city.latitude] as [number, number],
          },
        })),
    }),
    [mappedCities],
  );

  const staticCollection = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: mappedCities.map((city) => ({
        type: "Feature" as const,
        geometry: {
          type: "Point" as const,
          coordinates: [city.longitude, city.latitude] as [number, number],
        },
        properties: {
          name: city.header,
        },
      })),
    }),
    [mappedCities],
  );

  const allCoordinates = useMemo(
    () => mappedCities.map((city) => [city.longitude, city.latitude] as [number, number]),
    [mappedCities],
  );

  const routeHealth = useMemo(() => {
    if (!plan?.ordered_city_names?.length || !plan.drive_hours_matrix) return null;
    const expectedLegs = Math.max(plan.ordered_city_names.length - 1, 0);
    const normalizedLabels = plan.ordered_city_names.map((label) => {
      const [rawCity, rawState = ""] = label.split(",").map((value) => value.trim());
      return compactCityLabel(rawCity, rawState);
    });
    const legs = plan.ordered_city_names
      .map((label, index, arr) => {
        if (index === arr.length - 1) return null;
        const next = arr[index + 1];
        const hours = plan.drive_hours_matrix?.[label]?.[next];
        return typeof hours === "number" ? hours : null;
      })
      .filter((value): value is number => typeof value === "number");
    if (!legs.length) return null;
    const longest = Math.max(...legs);
    const average = legs.reduce((sum, value) => sum + value, 0) / legs.length;
    const missingLegs = Math.max(expectedLegs - legs.length, 0);
    const firstLabel = normalizedLabels[0];
    const lastLabel = normalizedLabels[normalizedLabels.length - 1];
    const ignoreFinalReturn = firstLabel && lastLabel && firstLabel === lastLabel;
    const seen = new Set<string>();
    let backtracks = 0;
    normalizedLabels.forEach((label, index) => {
      if (!label) return;
      if (index === normalizedLabels.length - 1 && ignoreFinalReturn) {
        return;
      }
      if (seen.has(label)) {
        backtracks += 1;
      } else {
        seen.add(label);
      }
    });
    return {
      expectedLegs,
      legs: legs.length,
      missingLegs,
      longest,
      average,
      backtracks,
    };
  }, [plan]);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) {
      return;
    }
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        glyphs: "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf",
        sources: {
          osm: {
            type: "raster",
            tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
            tileSize: 256,
            attribution: "© OpenStreetMap contributors",
          },
        },
        layers: [
          {
            id: "osm",
            type: "raster",
            source: "osm",
          },
        ],
      },
      center: [-104.9903, 39.7392],
      zoom: 4,
    });
    map.addControl(new maplibregl.NavigationControl(), "top-right");

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const removeLayer = (layerId: string) => {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
    };
    const removeSource = (sourceId: string) => {
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }
    };

    const showClusterLayers = canUseOsrm && viewMode === "clustered";

    if (!showClusterLayers) {
      removeLayer("routing-popular-circles");
      removeLayer("routing-popular-labels");
      removeSource("routing-popular");
      removeLayer("routing-outlier-circles");
      removeSource("routing-outliers");
      return;
    }

    const updateLayers = () => {
      const updateSource = (sourceId: string, collection: FeatureCollection, addLayers: () => void) => {
        if (map.getSource(sourceId)) {
          (map.getSource(sourceId) as maplibregl.GeoJSONSource).setData(collection);
        } else {
          map.addSource(sourceId, {
            type: "geojson",
            data: collection,
          });
          addLayers();
        }
      };

      updateSource("routing-popular", clusterCollection, () => {
        map.addLayer({
          id: "routing-popular-circles",
          type: "circle",
          source: "routing-popular",
          paint: {
            "circle-radius": 16,
            "circle-color": "#fb923c",
            "circle-stroke-width": 2,
            "circle-stroke-color": "#0f172a",
            "circle-opacity": 0.85,
          },
        });
        map.addLayer({
          id: "routing-popular-labels",
          type: "symbol",
          source: "routing-popular",
          layout: {
            "text-field": ["format", ["get", "name"], "\n", ["get", "count"], ["literal", " venues"]],
            "text-size": 10,
            "text-allow-overlap": true,
          },
          paint: {
            "text-color": "#0f172a",
          },
        });
      });

      updateSource("routing-outliers", outlierCollection, () => {
        map.addLayer({
          id: "routing-outlier-circles",
          type: "circle",
          source: "routing-outliers",
          paint: {
            "circle-radius": 5,
            "circle-color": "#facc15",
            "circle-stroke-width": 1,
            "circle-stroke-color": "#0f172a",
            "circle-opacity": 0.85,
          },
        });
      });
    };

    if (map.isStyleLoaded()) {
      updateLayers();
    } else {
      map.once("load", updateLayers);
    }
  }, [canUseOsrm, clusterCollection, outlierCollection, viewMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const sourceId = "routing-static";
    const layerId = "routing-static-circles";

    const cleanup = () => {
      if (!map) return;
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }
    };

    const showStaticLayer = !canUseOsrm || viewMode === "scattered";

    if (!showStaticLayer) {
      cleanup();
      return;
    }

    const addStaticLayer = () => {
      if (map.getSource(sourceId)) {
        (map.getSource(sourceId) as maplibregl.GeoJSONSource).setData(staticCollection);
      } else {
        map.addSource(sourceId, {
          type: "geojson",
          data: staticCollection,
        });
        map.addLayer({
          id: layerId,
          type: "circle",
          source: sourceId,
          paint: {
            "circle-radius": 6,
            "circle-color": "#facc15",
            "circle-stroke-width": 1.5,
            "circle-stroke-color": "#0f172a",
            "circle-opacity": 0.85,
          },
        });
      }
    };

    if (map.isStyleLoaded()) {
      addStaticLayer();
    } else {
      map.once("load", addStaticLayer);
    }

    return cleanup;
  }, [canUseOsrm, staticCollection, viewMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const sourceId = "routing-line";
    const layerId = "routing-line-layer";

    const cleanup = () => {
      if (!map) return;
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }
    };

    if (!useRealRouting || !routeSummary?.polyline) {
      cleanup();
      return;
    }

    const coordinates = decodePolyline(routeSummary.polyline);
    if (!coordinates.length) {
      cleanup();
      return;
    }

    const collection: FeatureCollection = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates,
          },
          properties: {},
        },
      ],
    };

    const addLine = () => {
      if (map.getSource(sourceId)) {
        (map.getSource(sourceId) as maplibregl.GeoJSONSource).setData(collection);
      } else {
        map.addSource(sourceId, {
          type: "geojson",
          data: collection,
        });
        map.addLayer({
          id: layerId,
          type: "line",
          source: sourceId,
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "#fb7185",
            "line-width": 3,
            "line-opacity": 0.9,
          },
        });
      }
    };

    if (map.isStyleLoaded()) {
      addLine();
    } else {
      map.once("load", addLine);
    }

    return cleanup;
  }, [routeSummary, useRealRouting]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !allCoordinates.length) return;
    const bounds = new maplibregl.LngLatBounds();
    allCoordinates.forEach((coordinate) => bounds.extend(coordinate));
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 60, duration: 0 });
    }
  }, [allCoordinates]);

  return (
      <Card className="aa-glass-card rounded-2xl">
      <CardHeader className="flex flex-col gap-3 px-6 pt-6 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle className="text-lg font-semibold tracking-wide text-foreground/90">Route map</CardTitle>
          {canUseOsrm && routeError ? <p className="text-xs text-amber-300">{routeError}</p> : null}
          <p className="text-xs text-muted-foreground">
            {canUseOsrm
              ? viewMode === "clustered"
                ? "Amber clusters highlight dense markets. Pale dots mark outliers to trim drive swings."
                : "Scatter view pins every target so you can inspect spacing and identify gaps."
              : "Static venue view. Upgrade to unlock drive-time optimization and AI clustering."}
          </p>
        </div>
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-3">
          <CapabilityGate
            flag="canUseRoutingOsrm"
            fallback={
              <Button size="sm" variant="outline" onClick={() => openUpgradeModal("Drive-time optimization")}>
                Unlock OSRM routing
              </Button>
            }
          >
            <Button
              size="sm"
              variant={useRealRouting ? "default" : "outline"}
              onClick={() => onToggleRealRouting(!useRealRouting)}
            >
              {useRealRouting ? "Real routing enabled" : "Use real routing (OSRM)"}
            </Button>
          </CapabilityGate>
        <CapabilityGate flag="canUseRoutingOsrm" variant="hide">
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={viewMode === "clustered" ? "default" : "outline"}
                onClick={() => setViewMode("clustered")}
              >
                Clustered view
              </Button>
              <Button
                size="sm"
                variant={viewMode === "scattered" ? "default" : "outline"}
                onClick={() => setViewMode("scattered")}
              >
                Scattered view
              </Button>
            </div>
          </CapabilityGate>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 px-6 pb-6">
        <div className="relative h-[320px] overflow-hidden rounded-2xl border border-border/5 bg-[#07090e]" ref={containerRef} />
        <CapabilityGate flag="canUseRoutingOsrm" variant="hide">
          {useRealRouting && routeSummary ? (
            <div className="grid gap-3 text-sm text-foreground/80 sm:grid-cols-2">
              <div className="rounded-xl border border-border/5 bg-background/5 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-foreground/70">Total distance</p>
                <p className="text-2xl font-semibold text-foreground">{routeSummary.distance_km.toFixed(1)} km</p>
              </div>
              <div className="rounded-xl border border-border/5 bg-background/5 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-foreground/70">Total duration</p>
                <p className="text-2xl font-semibold text-foreground">{routeSummary.duration_hours.toFixed(2)} h</p>
              </div>
            </div>
          ) : null}
        </CapabilityGate>
        {routeHealth ? (
          <div className="grid gap-3 text-sm text-foreground/80 sm:grid-cols-5">
            <div className="rounded-xl border border-border/5 bg-background/5 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-foreground/70">Total legs</p>
              <p className="text-2xl font-semibold text-foreground">{routeHealth.legs}</p>
            </div>
            <div className="rounded-xl border border-border/5 bg-background/5 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-foreground/70">Leg coverage</p>
              <p className="text-2xl font-semibold text-foreground">
                {routeHealth.legs}/{routeHealth.expectedLegs || 0}
              </p>
              {routeHealth.missingLegs ? (
                <p className="mt-1 text-xs text-amber-200">
                  {routeHealth.missingLegs} leg{routeHealth.missingLegs === 1 ? "" : "s"} missing
                </p>
              ) : null}
            </div>
            <div className="rounded-xl border border-border/5 bg-background/5 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-foreground/70">Max gap</p>
              <p className="text-2xl font-semibold text-foreground">{routeHealth.longest.toFixed(1)} h</p>
              {typeof maxDriveHours === "number" && routeHealth.longest > maxDriveHours ? (
                <p className="mt-1 text-xs text-amber-200">Over max drive hours</p>
              ) : null}
            </div>
            <div className="rounded-xl border border-border/5 bg-background/5 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-foreground/70">Avg leg</p>
              <p className="text-2xl font-semibold text-foreground">{routeHealth.average.toFixed(1)} h</p>
            </div>
            <div className="rounded-xl border border-border/5 bg-background/5 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-foreground/70">Backtracks</p>
              <p className="text-2xl font-semibold text-foreground">{routeHealth.backtracks}</p>
            </div>
          </div>
        ) : null}
        {routeHealth ? (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-primary-foreground dark:text-amber-100">
            <p className="font-semibold">Route quality checks</p>
            <ul className="mt-2 list-disc space-y-1 pl-4">
              {needsRefresh ? <li>Route is stale. Recalculate before syncing leads or locking stops.</li> : null}
              {routeHealth.missingLegs ? (
                <li>
                  {routeHealth.missingLegs} leg{routeHealth.missingLegs === 1 ? "" : "s"} missing from the drive matrix.
                </li>
              ) : null}
              {typeof maxDriveHours === "number" && routeHealth.longest > maxDriveHours ? (
                <li>Max gap exceeds your drive-hour limit.</li>
              ) : null}
              {routeHealth.backtracks ? <li>Backtracks detected. Consider regrouping into legs.</li> : null}
              {!needsRefresh &&
              !routeHealth.missingLegs &&
              !(typeof maxDriveHours === "number" && routeHealth.longest > maxDriveHours) &&
              !routeHealth.backtracks ? (
                <li>No issues detected.</li>
              ) : null}
            </ul>
          </div>
        ) : null}
        <CapabilityGate flag="canViewRoutingWarnings" variant="hide">
          {plan?.warnings?.length ? (
            <ul className="mt-2 list-disc space-y-1 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-primary-foreground dark:text-amber-100">
              {plan.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          ) : null}
        </CapabilityGate>
        {!canUseOsrm ? (
          <LockedFeatureCard
            title="Drive-time optimization"
            description="Upgrade to Pro for OSRM drive estimates, clustering, and warning insights."
            onUpgrade={() => openUpgradeModal("Drive-time optimization")}
          />
        ) : null}
      </CardContent>
    </Card>
  );
});

function decodePolyline(encoded: string): [number, number][] {
  if (!encoded) return [];
  let index = 0;
  let lat = 0;
  let lon = 0;
  const coordinates: [number, number][] = [];

  while (index < encoded.length) {
    let result = 0;
    let shift = 0;
    let byte: number;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20 && index < encoded.length);
    const deltaLat = result & 1 ? ~(result >> 1) : result >> 1;
    lat += deltaLat;

    result = 0;
    shift = 0;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20 && index < encoded.length);
    const deltaLon = result & 1 ? ~(result >> 1) : result >> 1;
    lon += deltaLon;

    coordinates.push([lon / 1e5, lat / 1e5]);
  }

  return coordinates;
}
